import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AdBreakView,
  AdPolicy,
  ChannelKind,
  LiveEventSlot,
  LiveEventWindow,
  LiveView,
  OfferPolicy,
  OfferView,
  PlaylistResume,
  PlaylistItem,
} from '@apm/contracts';
import {
  resolvePlayerKind,
  type ChannelReceiver,
  type InteractionEventType,
  type LiveSwitchTarget,
  type PlayerDebugSnapshot,
  type PlayerKind,
  type ReceiverAdDebugSnapshot,
  type ReceiverError,
  type ReceiverOfferDebugSnapshot,
  type ReceiverState,
} from '@apm/bloom-receiver-sdk';
import { isAutoplayRejection, markAutoStart } from './auto-start.js';
import { buffersUnlocked, markBuffersUnlocked } from './playback-buffers.js';
import {
  createFlowerReceiver,
  FlowerCreationAborted,
  createReceiver,
  type EngineFallbackReason,
  type ReceiverHandle,
} from './create-receiver.js';
import { config } from './config.js';
import { receiverEngineFor, type ReceiverEngine, type ReceiverEngineReason } from './receiver-engine.js';
import { createTelemetry, type Telemetry } from './telemetry.js';
import { liveSlotsFromWindows } from './epg-grid.js';

/**
 * 건너뛰기 버튼 상태를 폴링하는 주기(ms).
 *
 * SDK 의 `onTimeUpdate` 를 쓸 수 없다 — `PlayerAdapter.onTimeUpdate` 는 콜백 슬롯이
 * 하나뿐이고 **코어가 이미 그것을 점유한다.** UI 가 덮으면 코어의 `elapsedSec` 이 멈춰
 * `canSkip()` 이 영원히 false 가 된다. 그래서 판정 결과를 폴링한다.
 *
 * 250ms 면 남은 초 표시가 눈에 늦지 않고, 프레임(33ms) 대비 충분히 드물다.
 */
const SKIP_POLL_MS = 250;
/**
 * v33-d. 바이패스 중 「끝났나」를 묻는 주기 — 창의 끝이 이만큼 늦게 보일 수 있다.
 * v34. 창 목록 조회 시계의 틱이기도 하다(조회 자체의 주기는 리시버가 정한다).
 */
const LIVE_CHECK_MS = 1000;

export interface SkipButtonState {
  /** `skip.allowed === false` 면 버튼을 **숨긴다** (`docs/06-frontend-spec.md`). */
  visible: boolean;
  enabled: boolean;
  /** 비활성일 때 함께 보여줄 남은 초. 활성이면 0. */
  secondsLeft: number;
}

export interface ReceiverView {
  state: ReceiverState;
  item: PlaylistItem | null;
  index: number;
  total: number;
  /**
   * 지금 플레이리스트의 항목 전부 (v11). 「전체 상품」 시트가 이것을 그린다.
   *
   * **재생 가능한 것만 들어 있다** — 서버가 `isActive && status === 'ready'` 로 이미 걸렀고
   * (절대 규칙 1), 그래서 **목록에서 고른 것이 재생되지 않는 경우가 원리적으로 없다**
   * (절대 규칙 34).
   *
   * **`selectItem()` 이후에는 고른 상품이 맨 앞이다.** 서버가 회전시킨 제2 플레이리스트를
   * 그대로 받기 때문이고, 되돌리지 않는다 (`docs/27` D6).
   */
  items: readonly PlaylistItem[];
  /** 상품이 바뀔 때마다 값이 바뀐다. 오버레이를 닫는 트리거로 쓴다. */
  itemKey: string;
  skip: SkipButtonState;
  /** 화면을 오류로 덮어야 하는 상태. 복구되는 전환 실패는 여기에 오지 않는다. */
  fatalError: ReceiverError | null;
  playlistId: string | null;
  deviceId: string;
  started: boolean;
  /**
   * v29. **자동재생이 거부돼 제스처가 필요해졌다** (절대 규칙 72 의 자물쇠 ②).
   *
   * `started === false` 와 **다른 사실이다** — 전환도 잠깐 그 값이지만 그때 게이트를
   * 그리면 안 된다. 화면은 둘을 `showsStartGate` 에 함께 넘긴다.
   */
  gestureRequired: boolean;
  /**
   * v12 — 어느 재생 경로가 도는가. **화면에는 드러나지 않는 값이다** (`clientType` 과 같은
   * 성격). iOS 에서 영상이 나오지 않을 때 가장 먼저 알아야 하는 값이고, 그 기기에는 CDP
   * 하네스가 붙지 않아(`docs/29` §10) `?debug=1` 패널과 이 필드가 유일한 관찰 창이다.
   */
  playerKind: PlayerKind;
  /**
   * 이 채널의 종류 (v16). 목록을 받기 전에는 `null` — 그때는 아직 아무 버튼도 그리지 않는다.
   *
   * **화면이 이 값으로 버튼 구성을 가른다.** `null` 을 홈쇼핑으로 보지 않는 이유는
   * 일반 채널에서 잠깐 구매 버튼이 보였다 사라지는 것을 막기 위함이다.
   */
  channelKind: ChannelKind | null;
  /** 이어볼 지점 (v16). 토스트가 이것을 읽는다. 홈쇼핑이면 언제나 `null` (D5) */
  resume: PlaylistResume | null;
  /**
   * v21. 지금 재생 중인 광고 구간. **화면이 광고 UI 를 그리는 유일한 근거다**
   * (절대 규칙 50).
   *
   * ★ **광고 중에도 `item` 은 직전 회차를 유지한다.** 갈리는 것은 이 값의 유무 하나이고,
   * 그래서 게이트(`view.started && item !== null`)가 광고까지 통째로 삼킨다.
   */
  adBreak: AdBreakView | null;
  /**
   * v21. 이 채널의 광고 편성 정책. **`null` 이면 광고가 없다.**
   *
   * 시트의 「+광고 n분」 배지와 **설정의 광고 줄**이 이것을 본다 — 홈쇼핑 채널에는
   * 그 줄 자체가 없다(절대 규칙 56). **없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도
   * 광고가 있다」는 신호다.**
   */
  adPolicy: AdPolicy | null;
  /**
   * v23. 지금 떠 있는 상품 오버레이. **화면이 그것을 그리는 유일한 근거다**
   * (절대 규칙 59).
   *
   * ★ **카드가 떠 있어도 `item` 은 회차 그대로다** — 상품을 거기 넣으면 `send()` 가
   * `event.productId` 에 제휴사 상품 id 를 실어 그것이 `Event` 로 흘러간다.
   */
  offer: OfferView | null;
  /**
   * v23. 이 채널의 상품 노출 정책. **`null` 이면 상품 큐가 없다.**
   *
   * **설정의 「상품 추천」 줄이 이것을 본다** — 홈쇼핑 채널에는 그 줄 자체가 없다
   * (절대 규칙 66). **없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도 상품이 있다」는
   * 신호다.**
   */
  offerPolicy: OfferPolicy | null;
  /**
   * v33. **지금 라이브 이벤트를 나르는 중인가**(바이패스). 화면이 그것을 그리는 유일한
   * 근거다 (절대 규칙 83 · `adBreak`·`offer` 와 같은 자리).
   *
   * ★ **바이패스 중에도 `item` 은 회차 그대로다** (`docs/67` D24) — `null` 로 만들면
   * `view.started && item !== null` 게이트가 통째로 삼켜 **컨트롤 바가 사라지고**,
   * 영상만 나오므로 「라이브가 잘 나온다」로 보인다(F13 이 프리롤에서 겪은 그것).
   *
   * **없는 것 여섯이 이 값 하나로 갈린다** — 건너뛰기·전체 회차·구매·상품 카드·광고 UI·
   * 경과 시계(절대 규칙 85).
   */
  live: LiveView | null;
  /**
   * v34. **리시버가 지금 들고 있는 창들** — 1초 조회로 갈아 끼워진다(절대 규칙 87).
   *
   * ★ **재생 경로의 값이라 주소(`streamUrl`)가 있다.** 편성표가 지금 채널의 행을 그릴 때
   * **필드를 적어 주소를 뗀다**(`liveSlotsFromWindows` · 규칙 68). 스프레드로 옮기지 말 것.
   */
  liveWindows: readonly LiveEventWindow[];
  /**
   * v34-b. **재생 화면의 「라이브 ↔ 이어보기」 버튼이 지금 어디로 데려가는가** — `null` 이면 버튼이
   * 없다(`receiver.liveSwitchTarget()` · 판정은 코어 하나). 창이 열리고 닫히는 것은 사건이 없어
   * 아래 1초 시계가 다시 읽는다.
   */
  liveSwitch: LiveSwitchTarget | null;
  /**
   * v35. **시청자가 멈췄는가** (절대 규칙 90). 근거는 `receiver.isPaused()` 하나이고 `<video>.paused`
   * 를 읽지 않는다 — 더블 버퍼라 어느 엘리먼트인지 화면은 모른다. 전환이 풀면 `pausedChanged`
   * 로 온다(규칙 92).
   */
  paused: boolean;
  /**
   * v36. **지금 도는 엔진**(절대 규칙 93) — `receiverEngineFor()` 가 고르고, 폴백하면 `bloom` 이 된다. 화면은 이 값으로
   * 아무것도 가르지 않는다(디버그 패널·하네스만 읽는다).
   */
  engine: ReceiverEngine;
  /**
   * v36. **폴백의 이유** — Flower 를 골랐는데 BLOOM 이 돈다면 그 까닭(`bundle-load-failed` ·
   * `start-failed:<code>` · v37 부터 라이브 창은 사유가 아니다). **폴백은 조용하지 않다** — 콘솔 `warn` 과 이 값과 디버그 패널이 함께 말한다(§2.2).
   */
  engineFallback: EngineFallbackReason | null;
  /** v38. **처음 고른 엔진의 이유**(`receiverEngineFor`) — 설치된 앱이라 Flower 인지, 주소로 골랐는지. 디버그 패널·하네스만 읽는다 */
  engineReason: ReceiverEngineReason;
}

export interface ReceiverApi {
  view: ReceiverView;
  telemetry: Telemetry;
  /** 진입 화면의 "채널 시청 시작". 이 클릭이 자동재생 권한을 준다. */
  start: () => void;
  /** 이 항목부터 시작한다 (v25). `null` 이면 `start()` 와 같다 */
  startFrom: (at: string | null) => void;
  retry: () => void;
  skip: () => void;
  /** v21. **광고 스킵. 회차 스킵과 다른 버튼이다** */
  skipAd: () => void;
  /** v35. 녹화물 정지·재개·탐색 (`docs/71`). 라이브·광고 중에는 리시버가 무동작이다 */
  pausePlayback: () => void;
  /** 이름이 `resume` 이 아닌 것은 `view.resume`(이어보기 지점)과 갈라 두려는 것이다 */
  resumePlayback: () => void;
  seekBy: (deltaSec: number) => void;
  /** v21. 브레이크 축의 경과(초). 좌상단 시계가 폴링한다 */
  getAdBreakElapsedSec: () => number;
  /** 2026-10-29 · 라이브 앞뒤 광고 중 녹화물 잠금(`receiver.recordedLocked()` · `docs/45` §18.8) — 참이면 회차 선택이 무동작이다 */
  isRecordedLocked: () => boolean;
  /**
   * **지금 무엇이 어디까지 재생됐는가.** 셋을 **한 함수**로 낸다 (v27, 2026-09-04).
   *
   * ── ★ 왜 값이 아니라 함수인가 (값을 치르고 배웠다) ────────────
   *
   * `view` 는 렌더 시점의 스냅숏이고, **편성표 시트가 열려 있는 동안 `WatchScreen` 은
   * 다시 그려질 이유가 없다** — 시계는 `EpgScreen` 이 스스로 갖기 때문이다(그것이 v27 의
   * 성능 결정이었다). 그래서 시트에 값을 넘기면 **그 순간의 값이 영원히 얼어붙는다.**
   *
   * 세 번 같은 함정에 빠졌다.
   *
   * | 넘긴 것 | 프리롤 중에 시트를 열면 |
   * |---|---|
   * | `getElapsedSec` (함수) | **문제 없다** |
   * | `inAdBreak` (값) | 영원히 `true` → 앵커가 안 잡힌다 |
   * | `productId` (값) | 영원히 `null` → **앵커가 아예 안 잡힌다** |
   *
   * **그래서 셋을 함수 하나로 묶었다.** 이 창을 통과하는 값은 전부 살아 있다.
   *
   * 회차는 SDK 의 `getCurrentItem()` 에서 온다 — **광고 중에도 콘텐츠를 돌려주는**
   * 그 함수다(절대 규칙 50). 광고 여부는 `getAdBreak()` 하나가 정한다.
   */
  getPlayhead: () => {
    productId: string | null;
    elapsedSec: number;
    inAdBreak: boolean;
    /**
     * v33. **바이패스 중인가.** `inAdBreak` 과 **따로 둔다**(`docs/67` D25) — 이유가
     * 같아도(경과가 콘텐츠의 초가 아니다) **끊는 자리는 둘이다.** 한 칸으로 겸하면
     * 이름이 거짓이 되고, 언젠가 「광고인가」를 묻는 쪽이 라이브에서 참을 받는다.
     */
    inLive: boolean;
    /** v33-d. 바이패스에서 돌아갈 콘텐츠 초(`LiveView.resumeAtSec`). 아니면 `null` */
    liveResumeAtSec: number | null;
    /** v35. 시청자가 멈췄는가(`isPaused()`) — 편성표의 그 행은 흐르지 않는다(`docs/71` §2.6) */
    paused: boolean;
  } | null;
  /**
   * v34. **리시버가 지금 들고 있는 창을 편성표의 상자로** — 1초 조회의 새 값이다(절대 규칙 87).
   * `getPlayhead` 와 같은 이유로 함수다(시트가 열려 있는 동안 값은 얼어붙는다). **주소는 이미
   * 떼어져 있다**(`liveSlotsFromWindows` · 규칙 68). 리시버가 없으면 `null`.
   */
  getLiveSlots: () => LiveEventSlot[] | null;
  /**
   * v23. **시청자가 카드를 닫았거나 눌렀다.** 그 큐는 다시 열리지 않는다.
   *
   * **재생에 손대지 않는다** — 오버레이만 걷힌다 (절대 규칙 60).
   */
  dismissOffer: () => void;
  /**
   * v23. 카드가 걷히기까지 남은 초. **게터다** — 뷰에 실으면 `getOffer()` 이 매 틱
   * 새 객체를 만들어 `useEffect` 의존성이 매번 달라진다.
   */
  getOfferRemainingSec: () => number;
  /**
   * v23. 코어의 상품 상태. **화면에 증상이 없는 값이 여기 있다** —
   * `firedCues` · `pending` · **`activeCount`**(산술이 버린 큐의 수).
   */
  offerSnapshot: () => ReceiverOfferDebugSnapshot | null;
  /** 구매하기·정보보기. **재생을 멈추지 않는다** (CLAUDE.md 절대 규칙 7). */
  sendInteraction: (type: InteractionEventType) => void;
  /**
   * 특허 ④ 의 선택 전환. **v11 부터 화면이 부른다** — 「전체 상품」 시트에서 고른 상품이
   * 여기로 들어온다 (`docs/27` D1).
   *
   * **인덱스 이동이 아니다** (절대 규칙 33). 새 플레이리스트를 받아 교체하고 `select`
   * 이벤트를 내고 목록을 회전시킨다. 화면이 이것을 우회해 인덱스만 옮기면 **똑같이 그
   * 상품을 트므로 증상이 없고**, 사라지는 것은 그 셋이다.
   *
   * M7~v10 에는 부르는 화면 경로가 없어 `?debug=1` 핸들로만 내보냈고, 그 핸들은
   * **지금도 남아 있다** — `verify-integration` 의 `receiver.selectItemRotates` 가 화면
   * 없이 SDK 경로만 재는 유일한 자리다.
   */
  selectItem: (productId: string) => Promise<void>;
  /**
   * v33-b. **라이브 창에 합류한다** (`docs/67` §9.4). 포기했던 창이라도 다시 들어간다 —
   * 한 채널 안에서 라이브와 개인화 목록을 **오갈 수 있다**는 것이 이 문의 뜻이다.
   */
  joinLive: () => Promise<void>;
  /**
   * v33-b. **창을 두고 보던 회차로 돌아온다.** 창이 끝났을 때 저절로 가는 그 자리와
   * **같은 값**이다(절대 규칙 84) — `selectItem` 은 언제나 처음부터라 여기 쓸 수 없다.
   */
  resumeRecorded: () => Promise<void>;
  /**
   * 돌아왔을 때 서버의 이어보기 지점으로 다시 맞춘다 (v18).
   *
   * **`selectItem` 과 나란히 두지만 성격이 다르다** — 저쪽은 시청자가 고른 것이고 이쪽은
   * 다른 기기가 옮겨 놓은 것이다. `select` 이벤트도 새 `playlistId` 도 나오지 않는다.
   */
  resync: () => Promise<void>;
  /**
   * 디버그 패널이 읽는 어댑터 스냅샷. 패널이 꺼져 있으면 아무도 부르지 않는다.
   *
   * **v12 에서 구체 클래스를 가리키지 않게 됐다.** 어댑터가 둘이 됐으므로
   * `ReturnType<HlsPlayerAdapter['debugSnapshot']>` 는 네이티브 경로에서 거짓이 된다.
   */
  snapshot: () => PlayerDebugSnapshot | null;
  /**
   * v21/F13. 코어의 광고 상태. **재생기의 `snapshot` 과 다른 창이다** — 재생기는 광고를
   * 모르고(`armedAtSec` 이 그쪽의 전부다), 정책·발화한 시점·최소 간격은 코어가 갖는다.
   *
   * 화면에 증상이 없는 값 셋이 여기 있다 — `gapInMemoryOnly` · `firedCuePoints` ·
   * `correlator`.
   */
  adSnapshot: () => ReceiverAdDebugSnapshot | null;
  /**
   * 지금 항목의 경과 초 (v12). 좌상단 시계가 읽는다.
   *
   * **`ReceiverView` 의 필드가 아니라 함수다.** 필드로 두면 값이 바뀔 때마다 `WatchScreen`
   * 전체가 다시 그려지는데, 이 값은 1초에 한 번씩 바뀐다 — 시계가 **스스로 폴링하고
   * 자기만 다시 그리게** 하려고 함수로 낸다 (`snapshot` 과 같은 모양·같은 이유).
   */
  getElapsedSec: () => number;
  /**
   * v36. 엔진만의 진단 — Flower 면 조립된 재생목록(`playlistText`)·Flower 의 스냅숏·열리지 않은 브레이크(§5.5),
   * BLOOM 이면 `null`. 하네스(`verify-flower-receiver`)가 읽는다.
   */
  engineSnapshot: () => unknown;
}

const IDLE_SKIP: SkipButtonState = { visible: false, enabled: false, secondsLeft: 0 };

/**
 * 아직 쓰지 않은 첫 항목과 **그것이 어디서 왔는가** (v26 · 절대 규칙 70).
 *
 * 출처가 곧 조작이다 — 시청자가 고른 것은 `selectItem()`, 기기가 기억한 것은
 * `start({ from })`. **둘을 하나의 문자열로 합치면 그 갈림이 코드에서 사라진다.**
 */
type PendingStart = { kind: 'select' | 'resume'; productId: string } | null;

/** **고른 것이 기억을 이긴다** — 누른 것과 다른 것이 나오면 안 된다 */
function toPendingStart(startAt: string | null, resumeAt: string | null): PendingStart {
  if (startAt !== null && startAt !== '') return { kind: 'select', productId: startAt };
  if (resumeAt !== null && resumeAt !== '') return { kind: 'resume', productId: resumeAt };
  return null;
}

export function useReceiver(
  container: HTMLElement | null,
  channelId: string,
  /**
   * 진입 게이트를 거치지 않고 바로 시작한다. **채널 전환에서만 참이다** (v9, C8).
   *
   * 게이트는 자동재생 정책 때문에 있는데(`docs/06` 「진입 흐름」), 전환을 부른 것 자체가
   * 사용자 제스처이고 이미 이 문서에서 미디어가 재생된 뒤다. 첫 진입에서는 그대로 거친다 —
   * 거기서 건너뛰면 브라우저가 `play()` 를 거부하고 그 거부는 화면에 이유가 나오지 않는다.
   */
  autoStart = false,
  /**
   * BLOOM 세션 토큰 (v16). 있으면 플레이리스트 요청에 `Authorization` 이 실려
   * **계정 축 이어보기**가 온다. 없으면 `deviceId` 로 기기 축이다.
   *
   * **의존성에 들어간다** — 값을 안고 있는 어댑터를 만들기 때문이다. 빠뜨리면
   * 로그인 직후 그 화면에서만 계정 축이 안 붙고 채널을 한 번 바꾸면 고쳐진다.
   */
  authToken: string | null = null,
  /**
   * 편성표가 「이 채널의 **이 회차**부터」로 세운 경우 (v25 · 절대 규칙 69).
   * `null` 이면 지금까지의 시작 그대로다 — 「지금」 카드와 채널 시트가 그 길이다.
   *
   * ★ **의존성 배열에 넣지 않는다.** `autoStart` 를 뺀 그 이유가 여기서 **더 강하게**
   * 성립한다 — 그 값은 한 세션에 사실상 한 번 뒤집히지만 이것은 **편성표를 누를 때마다**
   * 바뀐다. 넣으면 같은 채널의 다른 회차마다 리시버가 통째로 다시 서고, 그 재생성은
   * `ChannelGate` 의 **「한 렌더를 비운다」를 거치지 않는다.**
   *
   * ★★ **한 번 쓰고 버린다.** `authToken` 은 의존성에 **있으므로** 로그인하면 이 효과가
   * 다시 도는데, 그때 다시 적용하면 **시청자가 누르지 않은 `select` 가 한 건 더** 나가고
   * 보던 영상이 0초로 되감긴다. 되살아나는 자리는 `start()`(이어보기)이고 그것이 맞다.
   */
  startAt: string | null = null,
  /**
   * **기기가 기억한 시작 항목** (v26 · 절대 규칙 70). 홈쇼핑 채널에서만 값이 온다 —
   * 자물쇠 ②(`entryStartFor`)가 `ChannelGate` 에서 이미 걸렀다.
   *
   * ★ **`startAt` 과 합치지 않는다.** 뜻이 둘인 값이 되면 그 둘을 가르는 분기가 화면
   * 쪽으로 흩어지고, 언젠가 **기억이 `selectItem()` 으로 흘러 부팅마다 `select` 한 건**이
   * 나간다 (절대 규칙 33). 여기서 한 번만 합쳐 `kind` 를 붙인다.
   *
   * **`startAt` 이 이긴다** — 시청자가 편성표에서 고르고 들어왔는데 기억이 그것을 덮으면
   * **누른 것과 다른 것이 나온다.**
   *
   * 의존성 배열에 넣지 않고 한 번 쓰고 버리는 것도 `startAt` 과 같다.
   */
  resumeAt: string | null = null,
): ReceiverApi {
  const telemetry = useMemo(createTelemetry, []);
  /** 아직 쓰지 않은 초기 회차. **소비의 주인은 이 ref 하나다** */
  const pendingStart = useRef<PendingStart>(toPendingStart(startAt, resumeAt));
  const consumedStartAt = useRef(false);
  /**
   * 기억한 항목의 **최신 값**. `startFrom(null)` 이 이것을 다시 본다 — 아래 ★.
   *
   * 의존성 배열에 넣지 않으려고 ref 다(`startFrom` 이 매 렌더 새로 만들어지면 게이트가
   * 그 함수를 prop 으로 들고 있어 쓸데없이 다시 그려진다).
   */
  const resumeAtRef = useRef<string | null>(resumeAt);
  resumeAtRef.current = resumeAt;
  // 마운트 뒤에 값이 들어오는 경로(게이트를 거치는 첫 진입)를 위해 **최신 값을 그대로**
  // 흘려 넣되, **이미 소비했으면 다시 채우지 않는다** — 위 ★★.
  //
  // ★ v25 — 「비어 있을 때만」이 아니라 **언제나** 덮는다. 게이트의 편성표에서 **두 번
  // 고르면** 첫 번째가 남아 **누른 것과 다른 것이 시작한다** — 그리고 화면은 멀쩡히
  // 그 채널을 튼다.
  if (!consumedStartAt.current) {
    pendingStart.current = toPendingStart(startAt, resumeAt);
  }
  /** v36 — 리시버와 그 플레이어를 함께 쥔 핸들(`createReceiver`). 정리·디버그 스냅샷이 여기로 간다 */
  const handleRef = useRef<ReceiverHandle | null>(null);
  // 기능 검사 하나이고 결과가 실행 중에 바뀌지 않는다 — 한 번만 정한다.
  const playerKind = useMemo(resolvePlayerKind, []);
  const receiverRef = useRef<ChannelReceiver | null>(null);
  /**
   * v36. **엔진은 한 번 고른다** — 기능 검사(`playerKind`)와 문서를 연 URL 의 `?engine=` 이 입력이고 둘 다 실행 중에
   * 바뀌지 않는다(규칙 93 · 저장하지 않는다). v38 — 문서가 설 때의 창 모양(`appWindowAtLoad` · 설치된 앱이면 Flower)도
   * 입력이고 역시 페이지에서 한 번 읽은 값이다.
   */
  const engineChoice = useMemo(
    () =>
      receiverEngineFor({
        playerKind,
        requested: config.requestedEngine,
        appWindow: config.appWindowAtLoad,
      }),
    [playerKind],
  );
  /** Flower 엔진이 서는 중인가(번들 적재 · `attachMedia`) — 그 사이의 시작은 미뤄 두고 붙는 순간 연다 */
  const flowerLoading = useRef(false);
  /** 서는 동안 눌린 시작 */
  const beginAsked = useRef(false);
  /**
   * 이 채널에서 마지막으로 연 문(`beginPlayback` 이 소비한 초기 회차 · `null` 이면 서버의 이어보기). 폴백한 BLOOM 이
   * **같은 문으로** 다시 연다 — 안 그러면 편성표에서 고른 회차가 폴백 한 번에 사라진다. `undefined` 면 아직 열지 않았다.
   */
  const lastBegin = useRef<PendingStart | undefined>(undefined);

  const [state, setState] = useState<ReceiverState>('idle');
  const [item, setItem] = useState<PlaylistItem | null>(null);
  const [index, setIndex] = useState(-1);
  const [items, setItems] = useState<readonly PlaylistItem[]>([]);
  const [skip, setSkip] = useState<SkipButtonState>(IDLE_SKIP);
  const [fatalError, setFatalError] = useState<ReceiverError | null>(null);
  const [started, setStarted] = useState(false);
  /**
   * ★ **자동재생이 거부돼 게이트로 되돌아왔는가** (2026-09-06 · 절대 규칙 72 의 자물쇠 ②).
   *
   * 지금까지 `setStarted(false)` 하나가 **「안 틀었다」와 「제스처가 필요하다」를 겸했다.**
   * 그런데 전환도 「안 틀었다」이므로, 그 하나로는 **게이트를 그릴 자리와 안 그릴 자리를
   * 가를 수 없었다** — 그것이 전환마다 게이트가 한 프레임 보이던 이유다(`showsStartGate`).
   */
  const [gestureRequired, setGestureRequired] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [channelKind, setChannelKind] = useState<ChannelKind | null>(null);
  const [resume, setResume] = useState<PlaylistResume | null>(null);
  const [adBreak, setAdBreak] = useState<AdBreakView | null>(null);
  /** v33. 바이패스. **화면의 없는 것 여섯이 이 값 하나로 갈린다** (절대 규칙 85) */
  const [live, setLive] = useState<LiveView | null>(null);
  const [liveWindows, setLiveWindows] = useState<readonly LiveEventWindow[]>([]);
  const [liveSwitch, setLiveSwitch] = useState<LiveSwitchTarget | null>(null);
  const [paused, setPaused] = useState(false);
  const [adPolicy, setAdPolicy] = useState<AdPolicy | null>(null);
  const [offer, setOffer] = useState<OfferView | null>(null);
  const [offerPolicy, setOfferPolicy] = useState<OfferPolicy | null>(null);
  const [engine, setEngine] = useState<ReceiverEngine>(engineChoice.engine);
  const [engineFallback, setEngineFallback] = useState<EngineFallbackReason | null>(null);

  /**
   * 재생을 연다. **`start()` 와 `selectItem()` 을 가르는 유일한 자리다** (v25, 절대 규칙 69).
   *
   * 부르는 곳이 둘이다 — 아래 생성 효과(`autoStart` 경로)와 진입 게이트의 `start()`.
   * **둘 다 지나야 한다**: 게이트를 거치는 첫 진입에서 이 함수를 안 쓰면 **고른 회차가
   * 조용히 사라지고** 그 채널의 이어보기 지점이 대신 뜬다 — 오류도 로그도 없다.
   *
   * ★ **`start()` 를 먼저 부르고 `selectItem()` 을 잇지 않는다.** 그러면 플레이리스트를
   * 두 번 받고, 안 고른 회차가 잠깐 뜨고, **`start` 이벤트가 두 건**이 되어
   * `verify-channels` 의 `switchDestroysOld` 가 **원인을 옛 인스턴스로 잘못 가리킨다.**
   *
   * `selectItem()` 은 `idle` 에서도 돈다 — 첫 줄이 `++this.gen` 이고 상태를 보지 않는다
   * (형제인 `skip()` 에는 `state !== 'playing'` 가드가 있는데 여기엔 없다). 냉시작 스왑은
   * `path='no-preroll'` 로 떨어지고 그때 `tFreezeMs` 가 `null` 인데, **앞 프레임이 없는
   * 전환의 T_freeze 는 정의되지 않으므로 그것이 옳다.**
   */
  const beginPlayback = useCallback((receiver: ChannelReceiver) => {
    const pending = pendingStart.current;
    pendingStart.current = null;
    consumedStartAt.current = true;
    lastBegin.current = pending;
    // ★ 빠뜨리면 컨트롤 바도, 스킵 폴링(`if (!started) return`)도 영영 안 돈다 —
    //   게이트가 남는 것은 시끄러운데 **「건너뛰기 버튼이 영영 안 뜬다」는 조용하다.**
    setStarted(true);
    // **여기가 그 반대편이다** — 시청자가 게이트를 눌렀거나 전환이 시작했다.
    setGestureRequired(false);
    if (pending === null) return receiver.start();
    /*
     * v26. **문이 셋이 됐고 여기가 그 갈림의 유일한 자리다** (절대 규칙 70).
     *
     * `select` — 시청자가 편성표·시트에서 골랐다. `select` 이벤트 1건이 나가야 한다.
     * `resume` — 기기가 기억했다. **`select` 가 나가면 안 된다** — 부팅마다 한 건씩
     *   쌓이면 절대 규칙 33 이 지키려는 그 지표가 매 실행 오염되고, **화면으로는
     *   두 길이 완전히 같다.**
     */
    return pending.kind === 'select'
      ? receiver.selectItem(pending.productId)
      : receiver.start({ from: pending.productId });
  }, []);

  // 어댑터와 리시버는 컨테이너가 생긴 뒤 **한 번만** 만든다. 두 번 만들면 `<video>` 가
  // 네 개가 되어 디코더가 둘로 나뉘고, 그 상태의 T_freeze 는 아무 의미가 없다.
  // (같은 이유로 `main.tsx` 에서 `StrictMode` 를 쓰지 않는다.)
  useEffect(() => {
    if (container === null || receiverRef.current !== null) return;

    /*
     * v36 — **만드는 자리는 `create-receiver.ts` 하나다** (절대 규칙 93). 어댑터 선택(`createPlayerAdapter`)·
     * `<video>` 한 쌍(v19)·오디오 토글 되걸기(v22)·광고/상품 어댑터의 언제나-주입(v21·v23)이 그 안으로 갔고
     * 근거 주석도 함께 옮겼다. 엔진은 `engineChoice` 가 정한다 — BLOOM 은 지금처럼 **동기로** 붙고, Flower 는 번들과
     * `attachMedia` 를 기다려 **비동기로** 붙는다. 아래 `attach` 가 배선의 유일한 자리라 두 엔진이 같은 화면 상태를 낸다.
     */
    let disposed = false;
    let fellBack = false;
    let current: ReceiverHandle | null = null;
    // v36 — 서는 중인 Flower 를 멈추는 신호(`create-receiver.ts` 의 줄 · 2026-10-20). 정리 함수가 당긴다.
    const leaving = new AbortController();
    lastBegin.current = undefined;
    beginAsked.current = false;
    setEngineFallback(null);

    const attach = (handle: ReceiverHandle): void => {
      const receiver = handle.receiver;

      receiver.on('stateChanged', (next) => {
        setState(next);
        // 오류 상태를 벗어나면 오류 화면도 걷는다.
        if (next === 'playing' || next === 'loading') setFatalError(null);
        /*
         * **소리 있는 재생이 실제로 붙은 순간을 표시한다** (v19). 그 뒤로는 같은 엘리먼트를
         * 계속 쓰므로 진입 게이트가 다시 필요하지 않다 — 채널 전환도, 상품 화면에서
         * `[방송 보기]` 로 돌아오는 것도 같은 문서다(`ChannelGate` 의 `autoStart` 초기값).
         *
         * **`start()` 를 부른 자리가 아니라 여기다** — 클릭했다고 허가가 생기는 것이 아니라
         * `play()` 가 성립해야 생긴다. 거부됐는데 표시해 두면 다음 복귀가 조용히 실패한다.
         */
        if (next === 'playing') markBuffersUnlocked();
      });
      receiver.on('itemChanged', (changed, at) => {
        setItem(changed);
        setIndex(at);
        setPlaylistId(receiver.getPlaylistId());
      });
      receiver.on('adBreakChanged', (view) => {
        setAdBreak(view);
      });
      // v33. 바이패스가 시작됐거나 끝났다. **화면은 이 값 하나로 그린다** (절대 규칙 83).
      receiver.on('liveChanged', (view) => {
        setLive(view);
        // v34-b. 들어가고 나오는 것(창의 끝 · 이어보기 · 합류 · 회차 선택)이 버튼을 뒤집는다.
        setLiveSwitch(receiver.liveSwitchTarget());
      });
      /*
       * v34. 창 목록이 새로 왔다(1초 조회). **내용이 같으면 상태를 바꾸지 않는다** — 매초 새 배열을
       * 넣으면 시청 화면 전체가 매초 다시 그려진다(바뀌는 것은 대개 아무것도 없다).
       */
      let lastWindowsKey = '';
      const takeWindows = (windows: readonly LiveEventWindow[]) => {
        // v34-b. 창이 사라지면(지웠다 · 끝났다) 1초 시계도 멈추므로 여기서 버튼을 걷는다.
        setLiveSwitch(receiver.liveSwitchTarget());
        const key = JSON.stringify(windows);
        if (key === lastWindowsKey) return;
        lastWindowsKey = key;
        setLiveWindows(windows);
      };
      receiver.on('liveWindowsChanged', takeWindows);
      // v23. 카드가 뜨거나 걷혔다. **화면은 이 값 하나로 그린다** (절대 규칙 59).
      receiver.on('offerChanged', (view) => {
        setOffer(view);
      });
      // v35. 정지는 이 사건 하나로 온다 — `stop()` 도 풀어서 알리므로 채널 전환 뒤에 남지 않는다.
      receiver.on('pausedChanged', setPaused);
      receiver.on('playlistLoaded', (loaded) => {
        // v16. 화면이 버튼 구성과 토스트를 이 둘로 가른다.
        setChannelKind(receiver.getChannelKind());
        setResume(receiver.getResume());
        // v21. 시트의 배지와 설정의 광고 줄이 이것을 본다.
        setAdPolicy(receiver.getAdPolicy());
        // v23. 설정의 상품 추천 줄이 이것을 본다 — 홈쇼핑이면 그 줄 자체가 없다.
        setOfferPolicy(receiver.getOfferPolicy());
        // v34. 창 목록 — 조회 시계가 이것의 유무로 켜지고, 편성표가 지금 채널의 행을 그린다.
        takeWindows(receiver.getLiveWindows());
        // **v11 전에는 `setTotal(loaded.length)` 였다** — 목록을 받고도 길이만 남기고 버렸다.
        // 「전체 상품」 시트가 그릴 것이 여기서 온다. `total` 은 이제 파생값이라 둘이
        // 어긋날 자리가 없다.
        setItems(loaded);
        setPlaylistId(receiver.getPlaylistId());
      });
      receiver.on('transition', (m) => {
        telemetry.push.transition(m);
      });
      receiver.on('eventSent', (e) => {
        telemetry.push.event(e);
      });
      receiver.on('error', (e) => {
        telemetry.push.error(e);
        /*
         * ★★ **v26 U10 — 자동재생 거부는 오류가 아니라 게이트다** (절대 규칙 72, 자물쇠 ②).
         *
         * 설치된 앱이라 묻지 않고 시작했는데 브라우저가 거부했다. 그대로 두면
         * **「재생을 시작할 수 없다」 스크림 + [다시 시도]** 인데, 시청자에게 그것은
         * 고장이다 — 실제로는 **버튼 한 번이면 되는 상태**이고 그 버튼이 곧 게이트다.
         *
         * **`buffersUnlocked()` 로 좁히는 것이 계약이다.** 이 문서에서 이미 소리 있는 재생이
         * 붙은 뒤의 거부는 게이트가 고칠 수 있는 것이 아니고(허가는 이미 있다), 넓히면
         * 스왑·광고 복귀의 실패까지 게이트로 떨어져 **컨트롤 바가 사라지고 「보던 것이
         * 사라졌다」**가 된다.
         *
         * ★ **`consumedStartAt` 을 함께 내린다.** 재생이 시작된 적이 없으므로 그 초기 회차는
         * **소비되지 않았다** — 안 내리면 다음 렌더에서 `pendingStart` 가 다시 채워지지 않고,
         * 게이트를 누른 뒤 **기억한 상품 대신 편성 첫 상품**이 나온다(절대 규칙 70 의 문이
         * 조용히 닫힌다). 화면에는 아무 이유도 없다.
         */
        if (!buffersUnlocked() && isAutoplayRejection(e.cause)) {
          markAutoStart('refused');
          consumedStartAt.current = false;
          setStarted(false);
          // ★ 2026-09-06. **게이트를 부르는 것은 이 값 하나다.** `started` 는 전환에서도
          // 거짓이므로 그것으로 부르면 전환마다 게이트가 한 프레임 보인다.
          setGestureRequired(true);
          return;
        }
        // **복구되는 실패로 화면을 덮지 않는다.** `TRANSITION_ABANDONED` 는 코어가
        // `load()` 경로로 복구하는 중이라는 뜻이고, 재생이 이어지는데 오류 UI 를 띄우면
        // 멀쩡한 채널이 고장 난 것처럼 보인다. 오류 화면은 `state` 로만 판단한다.
        if (e.code === 'EMPTY_PLAYLIST' || receiver.getState() === 'error') setFatalError(e);
      });

      current = handle;
      handleRef.current = handle;
      receiverRef.current = receiver;
      setEngine(handle.engine);
      setDeviceId(receiver.getDeviceId());

      // 전환 뒤의 재시작. **인스턴스를 만든 자리에서 부른다** — 별도 효과로 빼면 그것이
      // 이 효과보다 먼저 돌 수 있고, 그때는 `receiverRef` 가 아직 비어 있다.
      // v36 — Flower 가 서는 동안 눌린 시작(`beginAsked`)과 폴백의 다시 열기도 여기서 연다(한 번).
      if (autoStart || beginAsked.current) {
        beginAsked.current = false;
        void beginPlayback(receiver);
      }
    };

    /*
     * v36 — **폴백은 한 번이고 조용하지 않다** (절대 규칙 93 · `docs/72` §2.2). 옛 인스턴스를 정리하고(규칙 27) BLOOM 을
     * 세운다. 이미 연 문이 있으면 **같은 문으로** 다시 연다(`lastBegin`). Flower 가 첫 프레임 전에만 부르므로 광고·카드는
     * 떠 있지 않지만, 화면 상태는 확실히 비운다. 폴백 뒤의 BLOOM 은 **바뀐 XHR 위에서** 돈다(번들은 페이지에 남는다 · §5.4).
     */
    const fallBack = (reason: EngineFallbackReason, cause: unknown): void => {
      if (disposed || fellBack) return;
      fellBack = true;
      flowerLoading.current = false;
      console.warn('[v36] Flower 엔진 → BLOOM 폴백:', reason, cause);
      setEngineFallback(reason);
      const old = current;
      current = null;
      handleRef.current = null;
      receiverRef.current = null;
      old?.destroy();
      setAdBreak(null);
      setOffer(null);
      setPaused(false);
      if (lastBegin.current !== undefined) {
        pendingStart.current = lastBegin.current;
        consumedStartAt.current = false;
        beginAsked.current = true;
      }
      attach(createReceiver('bloom', { container, channelId, authToken }));
    };

    if (engineChoice.engine === 'flower') {
      flowerLoading.current = true;
      createFlowerReceiver({ container, channelId, authToken, onFallback: fallBack, signal: leaving.signal }).then(
        (handle) => {
          flowerLoading.current = false;
          if (disposed || fellBack) {
            handle.destroy();
            return;
          }
          attach(handle);
        },
        (e: unknown) => {
          // 물러나서 멈춘 생성은 폴백이 아니다 — 이 실행은 이미 끝났다(`fallBack` 의 `disposed` 도 막는다).
          if (e instanceof FlowerCreationAborted) return;
          fallBack('bundle-load-failed', e);
        },
      );
    } else {
      attach(createReceiver('bloom', { container, channelId, authToken }));
    }

    return () => {
      disposed = true;
      leaving.abort();
      flowerLoading.current = false;
      current?.destroy();
      current = null;
      handleRef.current = null;
      receiverRef.current = null;
    };
    // **채널이 바뀌면 정리가 먼저 돈다** (절대 규칙 27). 위 정리 함수가
    // `handle.destroy()`(= `receiver.stop()` → `player.destroy()`)를 하고 ref 를 비우므로, 새 리시버가
    // 서기 전에 옛 `<video>` 둘과 hls 인스턴스가 사라진다. 빠뜨리면 두 채널의
    // 프레임 콜백이 겹쳐 돌고 그 상태의 T_freeze 는 아무 의미가 없다.
    // **`autoStart` 는 의존성에 없다.** 그 값이 바뀌었다고 리시버를 다시 세울 이유가
    // 없고, 넣으면 첫 전환 뒤 `false → true` 로 **한 번 더 세운다** — 규칙 27 을 지키려고
    // 만든 절차가 스스로 인스턴스를 둘 만드는 셈이 된다. 이 훅에 exhaustive-deps 린트가
    // 걸려 있지 않으므로(이 저장소는 그 플러그인을 쓰지 않는다) 여기가 유일한 기록이다.
    // **`startAt`·`beginPlayback` 도 같은 이유로 없다** (v25, 절대 규칙 69) — 넣으면 같은
    // 채널의 다른 회차를 편성표에서 고를 때마다 리시버가 통째로 다시 서고, 그 재생성은
    // `ChannelGate` 의 「한 렌더를 비운다」를 **거치지 않는다.**
    // v36 — `engineChoice` 는 페이지에서 한 번 정해져 바뀌지 않는다(넣어도 다시 서지 않지만 넣지 않는다).
  }, [container, telemetry, channelId, authToken]);

  /*
   * ★ v21/F13 — **광고가 바뀌는 순간 다시 센다.**
   *
   * 폴링이 250ms 라, pod 안에서 광고가 넘어간 직후 최대 한 틱 동안 **앞 광고의 「누를 수
   * 있다」가 남는다.** 코어는 이미 새 광고의 `canSkipAd()` 로 거짓이므로 그동안의 클릭은
   * **조용한 무동작**이다 — 아래 주석이 경고하는 그 「눌리는데 아무 일도 안 일어나는
   * 버튼」이고, 원인만 다르다(재구현이 아니라 **낡음**).
   *
   * 시청자에게는 「건너뛰기가 가끔 안 먹는다」로 보이고, **광고가 바뀌는 그 0.25초에만**
   * 재현되므로 손으로는 잡히지 않는다. 키 하나로 이 효과를 다시 걸면 `tick()` 이 그
   * 자리에서 한 번 돌아 창이 닫힌다.
   */
  const adSkipKey = adBreak === null ? 'none' : `${adBreak.kind}:${String(adBreak.index)}`;

  // 건너뛰기 버튼 상태. 판정은 SDK 의 순수 함수를 그대로 쓴다 — 여기서 재구현하면
  // 버튼과 실제 동작이 어긋나 "눌리는데 아무 일도 안 일어나는 버튼"이 생긴다.
  useEffect(() => {
    if (!started) return;
    const tick = () => {
      const receiver = receiverRef.current;
      if (receiver === null) return;
      const remaining = receiver.secondsUntilSkippable();
      const next: SkipButtonState =
        remaining === null
          ? IDLE_SKIP
          : { visible: true, enabled: receiver.canSkip(), secondsLeft: remaining };
      setSkip((prev) =>
        prev.visible === next.visible &&
        prev.enabled === next.enabled &&
        prev.secondsLeft === next.secondsLeft
          ? prev
          : next,
      );
    };
    tick();
    const id = window.setInterval(tick, SKIP_POLL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [started, adSkipKey]);

  /*
   * ★ v33-d. **바이패스 중에는 1초마다 리시버에게 「끝났나」를 묻는다** (`docs/68` §2.0-e).
   *
   * 끝을 보는 판정은 `timeupdate` 축에서 도는데, 라이브가 멈추면 그 축도 멈춘다 — 그러면
   * **창이 끝나도 녹화물로 돌아오지 않고**, 화면을 누를 때까지 검다(2026-09-23 무대).
   * 코어에는 타이머가 없으므로(절대 규칙 6) 시계는 여기서 쥔다. 나오는 쪽만 돈다
   * (`checkLive` 의 ★).
   */
  const inLive = live !== null;
  /*
   * ★ v34. **그 시계를 넓힌다 — 창이 있는 채널을 보는 동안 창 목록을 조회한다** (`docs/69` §7.1 ·
   * 절대 규칙 87). 틱은 1초이고, 조회는 리시버가 정한 주기(`livePollIntervalMs` — 가까우면 1초 ·
   * 멀면 30초)가 됐을 때만 한다. **창이 없는 채널에서는 시계 자체가 없다** — 요청 0건.
   *
   * 조회가 겹치지 않게 한 번에 하나만 보낸다(느린 망에서 1초 틱이 요청을 쌓지 않게).
   * `refreshLive()` 는 끝에 이탈 판정을 하고, 조회가 실패해도 `checkLive()` 가 매 틱 돈다.
   */
  const hasLiveWindows = liveWindows.length > 0;
  useEffect(() => {
    if (!inLive && !hasLiveWindows) return;
    let inFlight = false;
    let nextPollAtMs = 0;
    const id = window.setInterval(() => {
      const receiver = receiverRef.current;
      if (receiver === null) return;
      receiver.checkLive();
      // v34-b. 창이 열리고 닫히는 것은 사건이 없다 — 같은 값이면 React 가 다시 그리지 않는다.
      setLiveSwitch(receiver.liveSwitchTarget());
      const intervalMs = receiver.livePollIntervalMs();
      if (intervalMs === null || inFlight || Date.now() < nextPollAtMs) return;
      inFlight = true;
      /*
       * ★ **틱의 절반만큼 일찍 연다** (2026-09-24 · `docs/70` §3.5). 틱도 1초이고 주기도 1초라, 다음
       *   틱이 **1ms 라도 일찍** 오면(타이머는 999ms 로 오기도 한다) 그 틱을 건너뛰고 **2초 뒤**에야
       *   묻는다 — 실측 20초에 16건 · 간격에 2000ms 넷, 그리고 AWS 에서 종료 큐 → 녹화가 2038ms 였다.
       *   반 틱의 여유면 1초 주기는 매 틱, 30초 주기는 29.5초가 된다.
       */
      nextPollAtMs = Date.now() + intervalMs - LIVE_CHECK_MS / 2;
      void receiver.refreshLive().finally(() => {
        inFlight = false;
      });
    }, LIVE_CHECK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [inLive, hasLiveWindows]);

  const start = useCallback(() => {
    setFatalError(null);
    const receiver = receiverRef.current;
    if (receiver !== null) void beginPlayback(receiver);
    // v36 — Flower 가 아직 서는 중이면 붙는 순간 연다(`attach` 의 `beginAsked`).
    else if (flowerLoading.current) beginAsked.current = true;
  }, [beginPlayback]);

  /**
   * **이 항목부터 시작한다** (v25). 진입 게이트의 편성표가 부르는 자리다.
   *
   * `start()` 와 **같은 함수를 지난다** — 갈리는 것은 어디서 온 회차인가 하나이고,
   * 그래서 「시작」의 절차(`setStarted` · 광고 브레이크 판정 · 이벤트)가 두 벌이 되지 않는다.
   *
   * ★ **`start` 에 인자를 더하지 않았다.** `StartGate` 가 `onClick={onStart}` 로 넘기므로
   * 그러면 **`MouseEvent` 가 회차 id 자리에 들어온다** — 그리고 그것은 목록에 없는 값이라
   * 서버가 **조용히 무시하고 첫 회차를 튼다.** 화면에는 아무 이유도 안 나온다.
   *
   * `null` 이면 `start()` 와 같다 — 「지금」 카드가 그 길이고, 이어보기가 살아 있다
   * (절대 규칙 68).
   */
  const startFrom = useCallback(
    (at: string | null) => {
      setFatalError(null);
      const receiver = receiverRef.current;
      if (receiver === null) {
        // v36 — Flower 가 서는 중이면 고른 것을 남겨 두고 붙는 순간 연다(`attach` 의 `beginAsked`).
        if (flowerLoading.current) {
          pendingStart.current = toPendingStart(at, resumeAtRef.current);
          beginAsked.current = true;
        }
        return;
      }
      /*
       * ★ v26 — **`at` 이 `null` 이어도 기억을 지우지 않는다.**
       *
       * 게이트의 편성표에서 홈쇼핑 채널의 **「지금」 카드**를 누르면 여기 `null` 이 온다
       * (첫 장에는 회차를 가리키지 않는다 — 절대 규칙 68). 그것을 그대로 `null` 로 덮으면
       * **기억이 그 클릭 하나로 사라지고 편성 첫 상품이 나온다** — 그런데 편성표의 그
       * 카드에는 기억한 상품이 그려져 있으므로 **화면이 방금 자기가 한 말을 어긴다.**
       */
      pendingStart.current = toPendingStart(at, resumeAtRef.current);
      void beginPlayback(receiver);
    },
    [beginPlayback],
  );

  const retry = useCallback(() => {
    setFatalError(null);
    void receiverRef.current?.start();
  }, []);

  /**
   * v21. **`skip()` 이 `Promise<void>` 가 됐다** — 조작 정책이 광고를 열면 즉시형 요청이
   * 최대 3초를 기다린다 (`docs/45` §8.6).
   *
   * **새 UI 가 없다.** 기다리는 동안 리시버가 `state === 'switching'` 이라 앱이 이미
   * 그것을 그린다. 여기서 `void` 로 흘려보내는 것은 화면이 그 응답을 기다릴 이유가
   * 없어서다 — 결과는 `stateChanged` 와 `adBreakChanged` 로 온다.
   */
  const skipNow = useCallback(() => {
    void receiverRef.current?.skip();
  }, []);

  /**
   * v21. **광고를 건너뛴다. `skip()` 과 다른 메서드다** (`docs/45` §8.5) — 하나로 겸하면
   * `send('skip')` 이 **회차 이탈률을 광고 스킵으로 부풀린다.**
   */
  const skipAdNow = useCallback(() => {
    void receiverRef.current?.skipAd();
  }, []);

  /** v35. 녹화물 정지·재개·탐색 (`docs/71` · 절대 규칙 90~92) */
  const pausePlayback = useCallback(() => {
    receiverRef.current?.pause();
  }, []);
  const resumePlayback = useCallback(() => {
    void receiverRef.current?.resume();
  }, []);
  const seekBy = useCallback((deltaSec: number) => {
    receiverRef.current?.seekBy(deltaSec);
  }, []);

  const sendInteraction = useCallback((type: InteractionEventType) => {
    receiverRef.current?.sendInteraction(type);
  }, []);

  const selectItem = useCallback(
    async (productId: string) => receiverRef.current?.selectItem(productId),
    [],
  );

  /**
   * v33-b. **한 채널 안에서 라이브와 개인화 목록을 오간다** (`docs/67` §9.4).
   *
   * 화면은 편성표의 두 칸을 그대로 누른다 — 위 칸(라이브)이면 `joinLive`, 아래 칸에서
   * 지금 보던 회차면 `resumeRecorded`. 어느 쪽도 **리시버를 다시 세우지 않는다**(규칙 27
   * 의 전환 비용을 치르지 않는다).
   */
  const joinLive = useCallback(async () => receiverRef.current?.joinLive(), []);
  const resumeRecorded = useCallback(async () => receiverRef.current?.resumeRecorded(), []);

  /**
   * 서버의 이어보기 지점으로 다시 맞춘다 (v18).
   *
   * **부를지 말지는 화면이 정한다** (`resume-return.ts` 의 순수 판정). 여기서 조건을 걸면
   * 그 판정이 DOM 뒤로 숨어 테스트가 보지 못한다.
   */
  const resync = useCallback(async () => receiverRef.current?.resync(), []);

  const snapshot = useCallback(() => handleRef.current?.playerSnapshot() ?? null, []);
  const engineSnapshot = useCallback(() => handleRef.current?.engineSnapshot() ?? null, []);

  /**
   * v21/F13. 코어의 광고 상태. **재생기의 `snapshot` 과 다른 창이다** — 재생기는 광고를
   * 모르고(`armedAtSec` 이 그쪽의 전부다), 정책·발화한 시점·최소 간격의 출처는 코어가
   * 갖는다.
   *
   * **하네스가 없으면 잴 수 없는 것 셋이 여기 있다** — `gapInMemoryOnly`(변이 7 이
   * 뒤집는 값) · `firedCuePoints`(중간 광고가 두 번 나는 것) · `correlator`.
   * 셋 다 **화면에 아무 증상이 없다.**
   */
  const adSnapshot = useCallback(() => receiverRef.current?.adDebugSnapshot() ?? null, []);

  const getElapsedSec = useCallback(() => receiverRef.current?.getElapsedSec() ?? 0, []);

  /**
   * v23. **시청자가 카드를 닫았거나 눌렀다.** 재생에 손대지 않는다 (절대 규칙 60).
   */
  const dismissOffer = useCallback(() => {
    receiverRef.current?.dismissOffer();
  }, []);

  /**
   * v23. 남은 초. **`useCallback(…, [])` 이어야 한다** — 폴링하는 쪽의 `useEffect`
   * 의존성에 들어가므로 매 렌더 새 함수가 되면 인터벌이 매번 다시 걸린다.
   */
  const getOfferRemainingSec = useCallback(
    () => receiverRef.current?.getOfferRemainingSec() ?? 0,
    [],
  );

  /**
   * v23. 코어의 상품 상태. **하네스가 없으면 잴 수 없는 값이 여기 있다** —
   * `activeCount`(산술이 버린 큐의 수) · `firedCues` · `pending`. 셋 다 화면에 증상이 없다.
   */
  const offerSnapshot = useCallback(() => receiverRef.current?.offerDebugSnapshot() ?? null, []);

  /**
   * v21. 브레이크 축의 경과(초). **좌상단 시계가 250ms 로 폴링한다.**
   *
   * **`useCallback(…, [])` 이어야 한다** — 시계의 `useEffect` 의존성에 들어가므로 매
   * 렌더 새 함수가 되면 인터벌이 매번 다시 걸린다(`getElapsedSec` 이 같은 이유로 그렇다).
   *
   * **화면이 다시 계산하지 않는다** — 스킵 점프 보정까지 두 벌이 되고, 어긋난 시계는
   * **아무 오류도 내지 않는다** (D15).
   */
  /** 위 `getPlayhead` 주석의 그 창. **여기를 지나는 값은 전부 살아 있다** */
  const getPlayhead = useCallback(() => {
    const receiver = receiverRef.current;
    if (receiver === null) return null;
    return {
      // 광고 중에도 콘텐츠를 돌려준다 (절대 규칙 50)
      productId: receiver.getCurrentItem()?.productId ?? null,
      // ★ 광고 중에는 **광고의 초**다 — 읽는 쪽이 그것을 알고 써야 한다.
      //   v33. **바이패스 중에는 라이브의 초다** — 같은 사정이고 값은 더 크게 어긋난다.
      elapsedSec: receiver.getElapsedSec(),
      inAdBreak: receiver.getAdBreak() != null,
      inLive: receiver.getLive() != null,
      liveResumeAtSec: receiver.getLive()?.resumeAtSec ?? null,
      paused: receiver.isPaused(),
    };
  }, []);

  const getLiveSlots = useCallback(() => {
    const receiver = receiverRef.current;
    return receiver === null ? null : liveSlotsFromWindows(receiver.getLiveWindows());
  }, []);

  const getAdBreakElapsedSec = useCallback(
    () => receiverRef.current?.getAdBreakElapsedSec() ?? 0,
    [],
  );

  const isRecordedLocked = useCallback(
    () => receiverRef.current?.recordedLocked() ?? false,
    [],
  );

  const view: ReceiverView = {
    state,
    gestureRequired,
    item,
    index,
    // **파생값이다.** 목록과 개수를 따로 들면 어긋날 수 있고, 그 어긋남은 「전체 상품
    // 시트의 항목 수가 디버그 패널의 총계와 다르다」로만 드러난다.
    total: items.length,
    items,
    itemKey: `${String(index)}:${item?.productId ?? ''}`,
    skip,
    fatalError,
    playlistId,
    deviceId,
    started,
    playerKind,
    channelKind,
    resume,
    adBreak,
    live,
    liveWindows,
    liveSwitch,
    paused,
    adPolicy,
    offer,
    offerPolicy,
    engine,
    engineFallback,
    engineReason: engineChoice.reason,
  };

  return {
    view,
    telemetry,
    start,
    startFrom,
    retry,
    skip: skipNow,
    skipAd: skipAdNow,
    pausePlayback,
    resumePlayback,
    seekBy,
    getAdBreakElapsedSec,
    isRecordedLocked,
    getPlayhead,
    getLiveSlots,
    dismissOffer,
    getOfferRemainingSec,
    offerSnapshot,
    sendInteraction,
    selectItem,
    joinLive,
    resumeRecorded,
    resync,
    snapshot,
    adSnapshot,
    getElapsedSec,
    engineSnapshot,
  };
}
